import { CardActionArea, CircularProgress, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import React from 'react';

import { LinkPreviewMetadataDetailsFragment } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { getLinkData } from '../../../../utils/links';
import { LinesEllipsis } from '../../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { usePlexRoute } from '../../details/usePlexRoute';

type Props = {
  href: string;
  linkText?: string | null;
  stopClickPropagation?: boolean;
  data:
    | { state: 'loading' }
    | { state: 'preview_unavailable' }
    | { state: 'done'; details: LinkPreviewMetadataDetailsFragment };
};

export const AnswerLinkPreview = React.memo(function AnswerLinkPreview({
  href,
  linkText,
  data,
  stopClickPropagation,
}: Props): React.ReactElement | null {
  const classes = useStyles();
  const [, updatePlexRoute] = usePlexRoute();

  if (data.state === 'loading') {
    return (
      <div className={data.state === 'loading' ? classes.contentLoading : undefined}>
        <div className={classes.contentLoader}>
          <CircularProgress size={CONTENT_SIZE} />
        </div>
      </div>
    );
  }

  if (data.state !== 'done') {
    return null;
  }

  if (data.details.__typename === 'ImageLinkMetadata') {
    // TODO
    return null;
  }

  const { title, subtitle, plexContentLink, LinkIcon } = getLinkData(data.details);

  const content = (
    <CardActionArea
      className={classes.cardActionAreaRoot}
      onClick={(event): void => {
        if (plexContentLink != null) {
          updatePlexRoute(plexContentLink);
        }

        if (stopClickPropagation) {
          event.stopPropagation();
        }
      }}
    >
      <div className={classes.mediaRoot}>
        <div className={classes.linkMetaData}>
          <LinkIcon className={classes.icon} />

          <div className={classes.mediaDescription}>
            <PlexTypography className={classes.mediaTitle} size={TextSizesEnum.Small}>
              <LinesEllipsis text={title || linkText || href} maxLine={2} />
            </PlexTypography>

            <PlexTypography className={classes.mediaSubTitle} size={TextSizesEnum.Small} text={subtitle} />
          </div>
        </div>
      </div>
    </CardActionArea>
  );

  if (plexContentLink == null) {
    // External link
    return (
      <a
        href={href}
        className={classNames(classes.root, classes.linkHref)}
        tabIndex={-1}
        target={'_blank'}
        rel={'noopener noreferrer'}
      >
        {content}
      </a>
    );
  } else {
    // Internal link handled inside content
    return <div className={classes.root}>{content}</div>;
  }
});

const CONTENT_SIZE = 60;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
    marginBottom: spacings.normal,
    color: 'inherit', // Fix ripple color
  },
  cardActionAreaRoot: {
    height: 64,
    display: 'flex',
    backgroundColor: '#fff',
    border: '2px solid #D0DAFF',
    borderRadius: 8,
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'space-between',
    paddingLeft: spacings.normal,
    paddingRight: spacings.normal,
  },
  mediaRoot: {
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaType: {
    display: 'flex',
    position: 'absolute',
    backgroundColor: '#fff',
    borderRadius: 3,
  },
  contentLoading: {
    opacity: 0.5,
  },
  contentLoader: {
    position: 'absolute',
  },
  linkType: {
    display: 'flex',
  },
  icon: {
    flexShrink: 0,
  },
  linkMetaData: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  linkHref: {
    textDecoration: 'none',
  },
  mediaDescription: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: spacings.normal,
  },
  mediaTitle: {
    textOverflow: 'ellipsis',
    color: colorPalette.dark._900,
    fontWeight: 500,
    textDecoration: 'none !important',
  },
  mediaSubTitle: {
    textOverflow: 'ellipsis',
    color: colorPalette.purpleGray._500,
    fontWeight: 500,
    textDecoration: 'none !important',
  },
}));
